// wrapping the whole page into project board feature

import { useProjectBoard } from "@timenotes/shared/src/features/project-board"
import { TApiTasksFilters } from "@timenotes/shared/src/services/api-client/tasks-api"
import { useProjectSectionsQuery } from "@timenotes/shared/src/services/project-sections/queries/use-project-sections-query"
import { DEFAULT_TASKS_FILTERS } from "@timenotes/shared/src/services/tasks/hooks/use-tasks-filters-formik"
import { useTopNavigationLinks } from "hooks/layout"
import useSearchParams from "hooks/use-search-params"
import useNoInitialEffect from "hooks/useNoInitialEffect"
import { useEffect, useRef } from "react"
import { useQueryClient } from "react-query"
import { useHistory, useParams } from "react-router-dom"
import ProjectDetailsHeader from "../shared/components/project-details-header"
import TasksFiltersContainer from "../shared/tasks-filters-container"
import DraggableSections, { SortableSection } from "../tasks/draggable-sections"
import { horizontalListSortingStrategy, SortableContext } from "@dnd-kit/sortable"
import { UniqueIdentifier } from "@dnd-kit/core"
import { Board } from "./components/styled-components/board"
import { BoardLoading } from "./components/styled-components/board-loading"
import SectionColumn from "./components/section-column"
import AddSectionContainer from "./components/add-section-container"

const ProjectBoardPage = () => {
  const history = useHistory()

  const params = useParams<{
    projectId: string
  }>()

  const projectId = params.projectId as string

  useTopNavigationLinks([
    {
      label: "← BACK",
      onClick: () => {
        history.push("/projects")
      },
    },
  ])

  const queryClient = useQueryClient()

  // option to bind tasks filters to query params easily
  const {
    tasksFiltersFormik,
  } = useProjectBoard()

  const {
    projectSectionsQuery,
  } = useProjectSectionsQuery({
    projectId,
    filters: tasksFiltersFormik.values
  }, {
    refetchOnWindowFocus: true, // just confirm refetch needed
    staleTime: 5 * 60 * 1000 // 5 minutes
  })

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries(['project-sections', projectId, tasksFiltersFormik.values])
    }
  }, [])

  const [searchParams, setSearchParams] = useSearchParams<TApiTasksFilters>(undefined, DEFAULT_TASKS_FILTERS)

  useEffect(() => {
    tasksFiltersFormik.setValues(searchParams)
  }, [])

  useNoInitialEffect(() => {
    setSearchParams({
      ...tasksFiltersFormik.values,
      // special case for all being undefined, but not default value we want to send in filters API request 
      isBillable: tasksFiltersFormik.values.isBillable === undefined ? undefined : tasksFiltersFormik.values.isBillable, // TODO: this should not be needed?
      state: tasksFiltersFormik.values.state === undefined ? 'all' : tasksFiltersFormik.values.state,
    })
  }, [JSON.stringify(tasksFiltersFormik.values)])

  const projectSections = projectSectionsQuery.data?.projectSections || []

  return (
    <>
      <Board className="tasks-board" onDropCapture={() => { }}>
        <ProjectDetailsHeader projectId={projectId} activeSection="board" />

        <div
          className="page-content ant-card-sticky"
        >
          <TasksFiltersContainer
            projectId={projectId}
          />
        </div>

        <br />

        {projectSectionsQuery.isFetched ? (<>
          <div
            className="column-container"
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              gap: "1rem",
              overflow: 'auto',
              backgroundColor: "#fafafb",
            }}
          >
            <DraggableSections
              projectId={projectId}
              projectSections={projectSections}
            >
              <SortableContext
                key={projectId}
                items={projectSections.map((ps) => ps.id as UniqueIdentifier)}
                strategy={horizontalListSortingStrategy}
              >
                {projectSections.map((projectSection) => (
                  <SortableSection
                    key={projectSection.id}
                    id={projectSection.id}
                  >
                    <SectionColumn
                      projectSection={projectSection}
                    />
                  </SortableSection>
                ))}
              </SortableContext>

              <div
                style={{
                  marginRight: '30px',
                }}
              >
                <AddSectionContainer
                  projectId={projectId}
                />
              </div>
            </DraggableSections>

          </div>
        </>) : (<>
          <div style={{ padding: "2rem", display: "flex", gap: "2rem" }}>
            <BoardLoading style={{ width: "20rem" }} />
            <BoardLoading style={{ width: "20rem" }} />
            <BoardLoading style={{ width: "20rem" }} />
            <BoardLoading style={{ width: "20rem" }} />
          </div>
        </>)}

      </Board>

    </>
  )
}

export default ProjectBoardPage